/**
 * Design Tokens — Modern Enterprise Design System
 *
 * Refined tokens inspired by Linear, Vercel, and Raycast.
 * Clean, professional, and modern with generous whitespace
 * and subtle depth cues.
 */

// ── Spacing ───────────────────────────────────────────────────────────────────
export const SPACING_BASE = 8; // px

/**
 * Returns a spacing value based on the 8px base unit.
 * @param {number} factor - Scale factor (0.5 through 8)
 * @returns {string} Spacing value in px
 */
export const spacing = (factor) => `${SPACING_BASE * factor}px`;

// ── Semantic Colors ───────────────────────────────────────────────────────────
// Primary (professional blue — trustworthy, not trendy)
export const COLOR_PRIMARY = '#2563eb';
export const COLOR_PRIMARY_LIGHT = '#3b82f6';
export const COLOR_PRIMARY_DARK = '#1d4ed8';

// Secondary (cool violet)
export const COLOR_SECONDARY = '#7c3aed';
export const COLOR_SECONDARY_LIGHT = '#8b5cf6';
export const COLOR_SECONDARY_DARK = '#5b21b6';

// Success (emerald)
export const COLOR_SUCCESS = '#059669';
export const COLOR_SUCCESS_LIGHT = '#10b981';
export const COLOR_SUCCESS_DARK = '#047857';

// Warning (warm amber)
export const COLOR_WARNING = '#d97706';
export const COLOR_WARNING_LIGHT = '#f59e0b';
export const COLOR_WARNING_DARK = '#b45309';

// Danger (crisp red)
export const COLOR_DANGER = '#dc2626';
export const COLOR_DANGER_LIGHT = '#ef4444';
export const COLOR_DANGER_DARK = '#b91c1c';

// Info (sky)
export const COLOR_INFO = '#0284c7';
export const COLOR_INFO_LIGHT = '#0ea5e9';
export const COLOR_INFO_DARK = '#0369a1';

// Neutral (modern slate)
export const COLOR_NEUTRAL = '#64748b';
export const COLOR_NEUTRAL_LIGHT = '#94a3b8';
export const COLOR_NEUTRAL_DARK = '#334155';

// ── Surface Colors ────────────────────────────────────────────────────────────
export const COLOR_BG_DEFAULT = '#f9fafb';
export const COLOR_BG_PAPER = '#ffffff';
export const COLOR_BORDER = '#e5e7eb';
export const COLOR_BG_SLATE_LIGHT = '#f9fafb';

// ── Shadows (refined, softer, more professional) ──────────────────────────────
export const SHADOW = [
  'none',                                                              // 0
  '0 1px 2px 0 rgba(0,0,0,0.03)',                                    // 1
  '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)', // 2
  '0 2px 4px -1px rgba(0,0,0,0.04), 0 1px 3px -1px rgba(0,0,0,0.03)', // 3
  '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.03)', // 4
  '0 6px 10px -2px rgba(0,0,0,0.05), 0 3px 6px -3px rgba(0,0,0,0.04)', // 5
  '0 8px 15px -3px rgba(0,0,0,0.06), 0 4px 6px -4px rgba(0,0,0,0.04)', // 6
  '0 10px 20px -3px rgba(0,0,0,0.07), 0 4px 8px -4px rgba(0,0,0,0.04)', // 7
  '0 12px 25px -5px rgba(0,0,0,0.08), 0 5px 10px -5px rgba(0,0,0,0.04)', // 8
  '0 14px 28px -5px rgba(0,0,0,0.08), 0 6px 12px -5px rgba(0,0,0,0.05)', // 9
  '0 16px 32px -5px rgba(0,0,0,0.09), 0 8px 14px -6px rgba(0,0,0,0.05)', // 10
  '0 18px 36px -5px rgba(0,0,0,0.10), 0 8px 16px -6px rgba(0,0,0,0.05)', // 11
  '0 20px 40px -6px rgba(0,0,0,0.10), 0 10px 18px -7px rgba(0,0,0,0.05)', // 12
  '0 22px 44px -6px rgba(0,0,0,0.11), 0 10px 20px -7px rgba(0,0,0,0.06)', // 13
  '0 24px 48px -8px rgba(0,0,0,0.12), 0 12px 22px -8px rgba(0,0,0,0.06)', // 14
  '0 26px 52px -8px rgba(0,0,0,0.12), 0 12px 24px -8px rgba(0,0,0,0.06)', // 15
  '0 28px 56px -8px rgba(0,0,0,0.13), 0 14px 26px -8px rgba(0,0,0,0.07)', // 16
  '0 30px 60px -10px rgba(0,0,0,0.14), 0 14px 28px -10px rgba(0,0,0,0.07)', // 17
  '0 32px 64px -10px rgba(0,0,0,0.14), 0 16px 30px -10px rgba(0,0,0,0.07)', // 18
  '0 34px 68px -10px rgba(0,0,0,0.15), 0 16px 32px -10px rgba(0,0,0,0.08)', // 19
  '0 36px 72px -12px rgba(0,0,0,0.16), 0 18px 34px -12px rgba(0,0,0,0.08)', // 20
  '0 38px 76px -12px rgba(0,0,0,0.16), 0 18px 36px -12px rgba(0,0,0,0.08)', // 21
  '0 40px 80px -12px rgba(0,0,0,0.17), 0 20px 38px -12px rgba(0,0,0,0.09)', // 22
  '0 42px 84px -14px rgba(0,0,0,0.18), 0 20px 40px -14px rgba(0,0,0,0.09)', // 23
  '0 44px 88px -14px rgba(0,0,0,0.18), 0 22px 42px -14px rgba(0,0,0,0.10)', // 24
];

// ── Border Radii (slightly larger for modern feel) ────────────────────────────
export const RADIUS_SM = '6px';
export const RADIUS_MD = '8px';
export const RADIUS_LG = '12px';
export const RADIUS_XL = '16px';
export const RADIUS_2XL = '20px';
export const RADIUS_FULL = '9999px';

// ── Layout Dimensions ─────────────────────────────────────────────────────────
export const TOPBAR_HEIGHT = 56;
export const SIDEBAR_EXPANDED = 260;
export const SIDEBAR_COLLAPSED = 68;

// ── Breakpoints ───────────────────────────────────────────────────────────────
export const BP_MOBILE = 768;
export const BP_TABLET = 768;
export const BP_LAPTOP = 1024;
export const BP_DESKTOP = 1280;

// ── Animation Durations ───────────────────────────────────────────────────────
export const DURATION_FAST = '150ms';
export const DURATION_NORMAL = '200ms';
export const DURATION_SLOW = '300ms';

// ── Easing Curves ─────────────────────────────────────────────────────────────
export const EASE_DEFAULT = 'cubic-bezier(0.4, 0, 0.2, 1)';
export const EASE_IN = 'cubic-bezier(0.4, 0, 1, 1)';
export const EASE_OUT = 'cubic-bezier(0, 0, 0.2, 1)';
export const EASE_SPRING = 'cubic-bezier(0.175, 0.885, 0.32, 1.275)';
